import { useAnalytics } from "@codat/analytics";
import {
  Accordion,
  Button,
  CopyIcon,
  Skeleton,
  useToast,
} from "@codat/orchard-ui";
import { useGetBrandingQuery } from "api/queries/useGetBrandingQuery";
import useGetEmailTemplate from "hooks/query/useGetEmailTemplate";
import React from "react";

import styles from "./EmailTemplateAccordion.module.scss";

interface EmailTemplateAccordionProps {
  linkUrl: string | undefined;
  companyName: string;
  clientName: string;
  clientId: string;
}

const EmailTemplateAccordion: React.FC<EmailTemplateAccordionProps> = ({
  linkUrl,
  companyName,
  clientName,
  clientId,
}) => {
  const { sendEvent } = useAnalytics();
  const { addToast } = useToast();
  const [{ data: branding, loading: brandingLoading }] = useGetBrandingQuery();
  const { data: emailTemplate } = useGetEmailTemplate({
    linkUrl,
    companyName,
    clientName,
    clientId,
    buttonColor: branding?.colors?.primary,
  });

  const onEmailTemplateCopy = () => {
    // get email template from the DOM
    const emailTemplateString =
      document?.getElementById("email-template")?.innerHTML;

    // to copy rich text to clipboard, maintains styles and formatting
    const listener = (e: ClipboardEvent) => {
      if (!emailTemplateString) {
        return;
      }

      e.clipboardData?.setData("text/html", emailTemplateString);
      e.clipboardData?.setData("text/plain", emailTemplateString);
      e.preventDefault();
    };

    document.addEventListener("copy", listener);
    document.execCommand("copy");
    document.removeEventListener("copy", listener);

    sendEvent("Copy Email Template Button - Click");
    addToast({
      message: "Copied email template to clipboard",
      variant: "success",
    });
  };

  return (
    <div className={styles.emailTemplateContainer}>
      {!linkUrl || brandingLoading || !emailTemplate ? (
        <Skeleton width="100%" className={styles.emailTemplateAccordion} />
      ) : (
        <Accordion
          header="Email template"
          id="email-template"
          className={styles.emailTemplateAccordion}
        >
          {emailTemplate}
        </Accordion>
      )}
      {/* using button rather than ClipboardCopy because we need control of the onClick */}
      <Button
        label="Copy email template to clipboard"
        variant="primary"
        onClick={onEmailTemplateCopy}
        type="button"
        data-testid="email-template-copy"
        className={styles.emailTemplateCopyButton}
        isLoading={brandingLoading}
      >
        <CopyIcon />
      </Button>
    </div>
  );
};

export default EmailTemplateAccordion;
